import { apiEmployeeCustomTable } from "@/api/employees/employee-custom-table.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeEmploymentHistoryKeys } from "@/queries/employees/use-employee-employment-history.query.ts";
import { employeePositionsKeys } from "@/queries/employees/use-employee-positions.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { customFieldsKeys } from "@/queries/use-custom-fields.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiCFTableRowFilter,
  ApiFieldLinkedField,
  ApiFieldTypes,
} from "@/types/custom-field.types.ts";
import { ApiEmployeeCustomTableEditPayload } from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeeCustomTableKeys = createQueryKeys("employeeCustomTable", {
  search: (employeeId: number, tableId: number, filter: ApiFilterBase) => ({
    queryKey: [employeeId, tableId, filter],
    queryFn: async () => {
      const result = await apiEmployeeCustomTable.search(employeeId, tableId, filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  newRowPrefill: (employeeId: number, tableId: number, effectiveFrom: string | null) => ({
    queryKey: [employeeId, tableId, { effectiveFrom }],
    queryFn: async ({ signal }) => {
      const result = await apiEmployeeCustomTable.getNewRowPrefill(
        employeeId,
        tableId,
        effectiveFrom,
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEmployeeCustomTableNewRowPrefill = (
  employeeId: number | null | undefined,
  tableId: number,
  effectiveFrom: string | null,
  enabled: boolean
) => {
  return useQuery({
    ...employeeCustomTableKeys.newRowPrefill(employeeId ?? 0, tableId, effectiveFrom),
    enabled: enabled && !!employeeId,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useEmployeeCustomTableSearch = (
  employeeId: number,
  tableId: number,
  filter: ApiFilterBase,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeeCustomTableKeys.search(employeeId, tableId, filter),
    enabled,
  });
};

interface EmployeeCustomTableRowSaveParams {
  employeeId: number;
  tableId: number;
  rowId?: number;
  payload: ApiEmployeeCustomTableEditPayload;
  linkedFields?: ApiFieldLinkedField[];
}

export const useEmployeeCustomTableRowSave = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      employeeId,
      tableId,
      rowId,
      payload,
    }: EmployeeCustomTableRowSaveParams) => {
      const result = rowId
        ? await apiEmployeeCustomTable.updateRow(employeeId, tableId, rowId, payload)
        : await apiEmployeeCustomTable.addRow(employeeId, tableId, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, { employeeId, tableId, linkedFields = [] }) => {
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(employeeId, tableId, {}).queryKey.slice(0, 4),
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys
          .tableRows(tableId, {} as ApiCFTableRowFilter)
          .queryKey.slice(0, 3),
      });
      if (linkedFields.length === 0) return;
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employeeId).queryKey });
      if (linkedFields.includes(ApiFieldTypes.employee_employment_legal_entity)) {
        queryClient.invalidateQueries({
          queryKey: employeeEmploymentHistoryKeys.all(employeeId).queryKey,
        });
      }
      if (linkedFields.some((field) => field !== ApiFieldTypes.employee_employment_legal_entity)) {
        queryClient.invalidateQueries({
          queryKey: employeePositionsKeys.search(employeeId, 1).queryKey.slice(0, 3),
        });
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

interface EmployeeCustomTableRowDeleteParams {
  employeeId: number;
  tableId: number;
  rowId: number;
}

export const useEmployeeCustomTableRowDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ employeeId, tableId, rowId }: EmployeeCustomTableRowDeleteParams) => {
      const result = await apiEmployeeCustomTable.deleteRow(employeeId, tableId, rowId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, { employeeId, tableId }) => {
      queryClient.invalidateQueries({
        queryKey: employeeCustomTableKeys.search(employeeId, tableId, {}).queryKey.slice(0, 4),
      });
      queryClient.invalidateQueries({
        queryKey: customFieldsKeys
          .tableRows(tableId, {} as ApiCFTableRowFilter)
          .queryKey.slice(0, 3),
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
